// One reading of a My Drafts row, shared by every place that lists or opens one.
//
// GET /submissions/documents returns SubmissionsReadModels.Draft:
// {submissionId, assignmentId, title, taskType, responseType, subjectName, submissionCount}
//
// There is no `id` field, and `submissionId` is null unless the row is exactly one
// submission — so a bulk draft has neither. Reading `submissionId ?? id` (as every
// call site used to) yields undefined for those rows, which the sidebar then drops
// on its `!question?.id` guard and NewDocRoot pushes as `/documents/undefined`.
import { UNTITLED_DRAFT_TITLE, isUntitledDraftTitle } from './draftTitle';

/**
 * A stable key for a row. The assignment id is the fallback rather than a
 * generated one because it is what /drafts/:assignmentId is keyed on, so the
 * selected row and the URL agree without a second lookup.
 */
export const draftRowId = (row) => row?.submissionId ?? row?.assignmentId;

/**
 * Routing is on the count, never on `submissionId` being null: null-as-signal
 * cannot tell "many submissions" apart from "none yet", and both open the wizard
 * while a one-submission document opens the document. A row from before the count
 * existed is single-response, which is what it was.
 */
export const isMultiResponseDraft = (row) => (row?.submissionCount ?? 1) !== 1;

/** Where clicking this row goes, or null when the row names nothing openable. */
export const draftRoutePath = (row) => {
  if (isMultiResponseDraft(row)) {
    return row?.assignmentId ? `/drafts/${row.assignmentId}` : null;
  }
  return row?.submissionId ? `/documents/${row.submissionId}` : null;
};

// Subject-config JSON ended up in the title field of some existing rows; rendering
// it raw is how it used to leak into the sidebar.
const isSubjectConfigJson = (value) => {
  if (typeof value !== 'string' || !value.trim()) return false;
  const trimmed = value.trim();
  return (
    trimmed.startsWith('{') &&
    (trimmed.includes('subjectCode') || trimmed.includes('subjectName'))
  );
};

/** The row's question-derived name: its title, or the untitled placeholder. */
export const draftQuestionTitle = (row) => {
  const title = row?.title;
  return typeof title === 'string' &&
    title.trim() &&
    !isSubjectConfigJson(title) &&
    !isUntitledDraftTitle(title)
    ? title.trim()
    : UNTITLED_DRAFT_TITLE;
};

/** The name "Rename" gave the row, or null when it still goes by its question. */
export const draftRenamedTitle = (row) => {
  const name = row?.displayTitle;
  return typeof name === 'string' && name.trim() ? name.trim() : null;
};

/**
 * What a My Drafts row is called. A rename wins over the question because that is
 * the whole point of renaming: the sidebar name may differ from the question the
 * draft is marked against, and typing the question must not undo the rename.
 */
export const draftDisplayTitle = (row) =>
  draftRenamedTitle(row) ?? draftQuestionTitle(row);

// `subjects` entries are a string or a {subjectCode, subjectName} object, so both
// shapes have to be unwrapped before anything lowercases or renders one.
const subjectFromList = (row) => {
  const list = row?.assignment?.subjects || row?.subjects;
  const first = Array.isArray(list) && list.length ? list[0] : null;
  if (!first) return '';
  return typeof first === 'object'
    ? first.subjectName ?? first.name ?? first.subjectCode ?? first.code ?? ''
    : first;
};

const subjectOf = (row) =>
  String(
    subjectFromList(row) ||
      row?.subjectName ||
      row?.subject_name ||
      row?.subject ||
      row?.assignment?.subjectName ||
      row?.assignment?.subjectCode ||
      row?.subjectCode ||
      '',
  ).trim();

/** A subject that merely echoes the title tells the reader nothing. */
export const draftSubjectOf = (row) => {
  const subject = subjectOf(row);
  const title = row?.title;
  return title && subject && String(title).trim() === subject ? '' : subject;
};

/** The subjects the sidebar's filter can offer, given the rows it is showing. */
export const draftSubjectOptions = (rows) =>
  [...new Set((rows || []).map((row) => row?.subjectName).filter(Boolean))].sort();

/** One document row, in the shape the My Drafts sidebar renders. */
export const toDraftRow = (row) => {
  const subject = draftSubjectOf(row);
  return {
    id: draftRowId(row),
    assignmentId: row?.assignmentId ?? null,
    submissionId: row?.submissionId ?? null,
    submissionCount: row?.submissionCount ?? 1,
    // Both travel: the row renders displayTitle (falling back to title), while the
    // question editor and the subject filter keep reading title.
    title: draftQuestionTitle(row),
    displayTitle: draftRenamedTitle(row),
    taskType: row?.taskType,
    responseType: row?.responseType,
    // Both names are read downstream (the row renders one, the filter the other),
    // so they are one value under two keys rather than two computations.
    subject,
    subjectName: subject,
    status: row?.status,
    jeddaiFeedbackReceivedAt: row?.jeddaiFeedbackReceivedAt,
  };
};

const openSubmissionId = (submission) => submission?.id;

const openAssignmentId = (submission) =>
  submission?.assignmentId ?? submission?.assignment?.id;

const byExactSubmission = (rows, id) =>
  id ? rows.find((row) => row?.submissionId === id) : undefined;

const byAssignment = (rows, id) =>
  id ? rows.find((row) => row?.assignmentId === id) : undefined;

/**
 * Which My Drafts row the open document belongs to, as the id that row is keyed
 * on.
 *
 * /documents/:id is addressed by a SUBMISSION, but a bulk row has no
 * `submissionId` and is keyed on its ASSIGNMENT — so handing the sidebar the id
 * out of the URL matched no row at all, and opening one student of a bulk upload
 * highlighted nothing. The owning row is found by whichever of the two it
 * records.
 *
 * Exact submission first: a one-submission assignment is an ordinary document
 * whose row is keyed on the submission, and matching its assignment instead
 * would key the highlight on the wrong field.
 *
 * Falls back to the open submission id, because the rows arrive after the
 * document does and a single-response document is already correct under it.
 */
export const currentDraftRowId = (rows, submission) => {
  const list = Array.isArray(rows) ? rows : [];
  const owner =
    byExactSubmission(list, openSubmissionId(submission)) ??
    byAssignment(list, openAssignmentId(submission));
  return owner ? draftRowId(owner) : openSubmissionId(submission);
};
